/* tt-story.js - scroll-driven storytelling for the tt-story-* sections (first used by Cajo, 2026-10-16).
   One rAF loop reads scroll once per frame and hands each section its progress (0..1).
   Visitors who ask for reduced motion get every frame as a still: no pinning maths, no parallax. */
(() => {
  if (window.__ttStory) return;
  window.__ttStory = true;
  /* Scroll-driven effects run whatever the Reduce Motion setting says: the visitor's own scroll
     moves them (Josh, 2026-10-18, whose iPhone has it on: "needs the same animation as desktop").
     Only motion that runs by itself, the drifting contours and the hero's auto-advance, honours
     the setting (calm). */
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reduce = false;
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const parts = [];
  let ask = () => {};

  /* Pinned track: progress across the part of the track that scrolls past a full viewport. */
  const trackProgress = (el) => {
    const r = el.getBoundingClientRect();
    const span = r.height - window.innerHeight;
    return span > 0 ? clamp(-r.top / span) : 0;
  };
  /* Free-flowing element: 0 as it enters the bottom, 1 as it leaves the top. */
  const passProgress = (el) => {
    const r = el.getBoundingClientRect();
    return clamp((window.innerHeight - r.top) / (window.innerHeight + r.height));
  };

  /* ---- Type motion: a pinned statement sets itself as the visitor scrolls (--t 0..1) ---- */
  document.querySelectorAll('[data-tts-type]').forEach((root) => {
    if (reduce) { root.style.setProperty('--t', '1'); return; }
    /* data-tts-type-early (2026-10-19, Josh: a dead black screen before the Outpost 128's
       statements): the letters start as the statement comes up the screen, not once it has
       pinned, so the screen it slides in on is never empty. --t runs 0 to 1 from its top at
       90% of the window to the end of its pinned stretch. */
    const early = root.hasAttribute('data-tts-type-early');
    const earlyProgress = (el) => {
      const r = el.getBoundingClientRect(), vh = window.innerHeight;
      const from = vh * 0.9, span = from + r.height - vh;
      return span > 0 ? clamp((from - r.top) / span) : 0;
    };
    parts.push(() => root.style.setProperty('--t', (early ? earlyProgress(root) : trackProgress(root)).toFixed(4)));
  });

  /* ---- Sections tied to particular boats (data-show-boats): shown only while one of them is picked ---- */
  const boatGated = [...document.querySelectorAll('[data-show-boats]')];
  const gate = (key) => boatGated.forEach((el) => { el.hidden = !el.dataset.showBoats.split(',').map((k) => k.trim()).includes(key); });
  document.addEventListener('tts:boat', (e) => { gate(e.detail.key); ask(); });
  if (document.documentElement.dataset.boat) gate(document.documentElement.dataset.boat);

  /* ---- Colours: the picked boat's colours, wiped one into the next by the scroll ---- */
  document.querySelectorAll('[data-tts-colours]').forEach((root) => {
    const all = [...root.querySelectorAll('[data-colour]')];
    const list = root.querySelector('[data-colour-list]');
    const word = root.querySelector('[data-colour-word]');
    const boatLabel = root.querySelector('[data-colour-boat]');
    const seam = root.querySelector('.tts-colours__seam');
    let layers = []; let last = -1;
    /* A swatch is the picture's own median hull colour, read once the picture is in. */
    const swatch = (fig, dot) => {
      const img = fig.querySelector('img');
      if (!img) return;
      const read = () => {
        try {
          const c = document.createElement('canvas'); c.width = 120; c.height = Math.max(1, Math.round(120 * img.naturalHeight / img.naturalWidth));
          const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(img, 0, 0, c.width, c.height);
          const d = x.getImageData(0, 0, c.width, c.height).data; const px = [];
          for (let i = 0; i < d.length; i += 4) if (d[i + 3] > 250) px.push([d[i], d[i + 1], d[i + 2], d[i] + d[i + 1] + d[i + 2]]);
          px.sort((a, b) => a[3] - b[3]);
          const m = px[Math.floor(px.length * 0.62)];
          if (m) dot.style.setProperty('--sw', `rgb(${m[0]}, ${m[1]}, ${m[2]})`);
        } catch (e) { /* a picture from another origin cannot be read; the swatch keeps its outline */ }
      };
      if (img.complete && img.naturalWidth) read(); else img.addEventListener('load', read, { once: true });
    };
    const setBoat = (key, name) => {
      layers = all.filter((f) => f.dataset.boat === key);
      all.forEach((f) => { f.hidden = !layers.includes(f); });
      root.hidden = layers.length < 2;
      root.style.setProperty('--n', layers.length);
      /* the frame takes this boat's own proportions, from the picture's width and height */
      const im = layers[0]?.querySelector('img');
      if (im && im.getAttribute('width')) root.querySelector('[data-colour-boatbox]').style.aspectRatio = `${im.getAttribute('width')} / ${im.getAttribute('height')}`;
      if (boatLabel && name) boatLabel.textContent = name;
      list.innerHTML = '';
      layers.forEach((f, k) => {
        f.querySelector('img')?.setAttribute('loading', 'eager');
        const li = document.createElement('li');
        li.innerHTML = `<button type="button" class="tts-colours__pick" data-k="${k}"><span class="tts-colours__dot"></span><span class="tts-num">${String(k + 1).padStart(2, '0')}</span><span class="tts-colours__name">${f.dataset.name}</span></button>`;
        list.appendChild(li);
        swatch(f, li.querySelector('.tts-colours__dot'));
      });
      last = -1; ask();
    };
    list.addEventListener('click', (e) => {
      const b = e.target.closest('[data-k]'); if (!b) return;
      const r = root.getBoundingClientRect(); const span = r.height - window.innerHeight;
      const n = layers.length; const k = +b.dataset.k;
      window.scrollTo({ top: window.scrollY + r.top + span * (n > 1 ? k / (n - 1) : 0), behavior: reduce ? 'auto' : 'smooth' });
    });
    document.addEventListener('tts:boat', (e) => setBoat(e.detail.key, e.detail.name));
    if (document.documentElement.dataset.boat) setBoat(document.documentElement.dataset.boat, '');
    parts.push(() => {
      const n = layers.length; if (n < 2 || root.hidden) return;
      const p = trackProgress(root);
      root.style.setProperty('--p', p.toFixed(4));
      const seg = p * (n - 1);
      /* each colour holds, then the next wipes in from the stern: smoothstep over the middle half of each step */
      layers.forEach((f, k) => {
        if (k === 0) { f.style.clipPath = 'none'; return; }
        const t = clamp((seg - (k - 1) - 0.2) / 0.6); const e = reduce ? (seg >= k - 0.5 ? 1 : 0) : t * t * (3 - 2 * t);
        f.style.clipPath = `inset(0 0 0 ${((1 - e) * 100).toFixed(2)}%)`;
        f.style.setProperty('--e', e.toFixed(4));
      });
      const k = Math.min(n - 1, Math.max(1, Math.ceil(seg - 0.2)));
      const t = clamp((seg - (k - 1) - 0.2) / 0.6); const e = t * t * (3 - 2 * t);
      seam.style.left = ((1 - e) * 100).toFixed(2) + '%';
      seam.style.opacity = (e > 0.01 && e < 0.99) ? '1' : '0';
      const i = Math.min(n - 1, Math.round(seg));
      if (i !== last) {
        last = i;
        if (word) word.textContent = layers[i].dataset.name.split(' ')[0];
        list.querySelectorAll('[data-k]').forEach((b) => b.classList.toggle('is-on', +b.dataset.k === i));
      }
    });
  });

  /* ---- Scrub: runs of real footage frames, played forward and back by the scroll ---- */
  document.querySelectorAll('[data-tts-scrub]').forEach((root) => {
    const canvases = [...root.querySelectorAll('[data-act]')];
    const lines = [...root.querySelectorAll('[data-act-line]')];
    const stats = root.querySelector('[data-scrub-stats]');
    const n = canvases.length; if (!n) return;
    const acts = canvases.map((c) => ({ c, x: c.getContext('2d'), urls: c.dataset.frames.split('|'), imgs: [], drawn: -1 }));
    let loading = false;
    const load = () => {
      if (loading) return; loading = true;
      acts.forEach((a) => { a.imgs = a.urls.map((u, i) => { const im = new Image(); im.decoding = 'async'; im.src = u; if (i === 0 || i === a.urls.length - 1) im.onload = () => { a.drawn = -1; ask(); }; return im; }); });
    };
    new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) load(); }), { rootMargin: '150% 0px' }).observe(root);
    const size = () => acts.forEach((a) => { const r = a.c.getBoundingClientRect(); const d = Math.min(2, window.devicePixelRatio || 1); a.c.width = Math.round(r.width * d); a.c.height = Math.round(r.height * d); a.drawn = -1; });
    window.addEventListener('resize', () => { size(); ask(); });
    size();
    /* cover-fit the nearest loaded frame */
    const draw = (a, k) => {
      let i = k; while (i > 0 && !(a.imgs[i] && a.imgs[i].complete && a.imgs[i].naturalWidth)) i -= 1;
      const im = a.imgs[i]; if (!im || !im.naturalWidth || a.drawn === i) return;
      const W = a.c.width, H = a.c.height, s = Math.max(W / im.naturalWidth, H / im.naturalHeight);
      const w = im.naturalWidth * s, h = im.naturalHeight * s;
      const fx = (+a.c.dataset.fx || 50) / 100, fy = (+a.c.dataset.fy || 50) / 100;
      a.x.drawImage(im, (W - w) * fx, (H - h) * fy, w, h); a.drawn = i;
    };
    let on = -1;
    parts.push(() => {
      const p = trackProgress(root);
      /* each act takes an equal share; the last 15% of the track holds the close (numbers, button) */
      const q = clamp(p / 0.85);
      const seg = Math.min(n - 1, Math.floor(q * n));
      const t = reduce ? 1 : clamp(q * n - seg);
      if (seg !== on) { on = seg; canvases.forEach((c, k) => c.classList.toggle('is-on', k === seg)); lines.forEach((l, k) => l.classList.toggle('is-on', k === seg)); }
      const a = acts[seg]; draw(a, Math.round(t * (a.urls.length - 1)));
      if (stats) root.style.setProperty('--close', clamp((p - 0.7) / 0.2).toFixed(4));
    });
  });

  /* ---- No floating back-to-top (Josh, 2026-10-16: it "covers important content and it's
     annoying"). On the home page the logo does the job instead: it already points home, so a
     click while already home glides back to the top rather than reloading. ---- */
  if (document.querySelector('[data-tts-explorer]')) {
    const logo = document.querySelector('a.tt-nav__logo-plate');
    if (logo) logo.addEventListener('click', (e) => {
      if (window.location.pathname !== '/' || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  /* ---- In-page links glide (Josh, 2026-10-16: the hero's Pick your boat jumped to the picker).
     Any #link inside the story scrolls smoothly to its target; the address still updates. ---- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('.tts a[href^="#"]');
    if (!a || a.getAttribute('href').length < 2) return;
    const target = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', a.getAttribute('href'));
  });

  /* ---- Page-wide contours (Josh, 2026-10-16): when the hero has them, a fixed layer behind the
     whole page carries them into every large black area; it fades in once the hero has gone ---- */
  const heroAmbient = document.querySelector('.tts-hero [data-tts-contours]');
  if (heroAmbient && !document.querySelector('.tts-ambient--page')) {
    const pg = document.createElement('canvas');
    pg.className = 'tts-ambient tts-ambient--page'; pg.setAttribute('aria-hidden', 'true'); pg.dataset.ttsContours = '';
    document.body.prepend(pg); document.documentElement.classList.add('has-page-ambient');
    const hero = heroAmbient.closest('.tts-hero');
    parts.push(() => { const r = hero.getBoundingClientRect(); pg.style.setProperty('--past', clamp(-r.bottom / (window.innerHeight * 0.6) + 0.15).toFixed(3)); });
  }

  /* ---- The same page-wide layer for a page with no story hero (Cajo Contact and Claims, Josh,
     2026-10-16): a section marks itself data-tts-page-ambient and the layer is simply on. ---- */
  if (document.querySelector('[data-tts-page-ambient]') && !document.querySelector('.tts-ambient--page')) {
    const pg = document.createElement('canvas');
    pg.className = 'tts-ambient tts-ambient--page'; pg.setAttribute('aria-hidden', 'true'); pg.dataset.ttsContours = '';
    pg.style.setProperty('--past', '1');
    document.body.prepend(pg); document.documentElement.classList.add('has-page-ambient');
  }

  /* Phones stand the turn's top view bow-up (Josh, 2026-10-17): scale the 118vw-wide frame so the
     hull runs the height left under the words. CSS cannot divide a height by a width everywhere. */
  { const upS = () => document.documentElement.style.setProperty('--up-s', Math.max(0.6, (window.innerHeight - 290) / (1.18 * window.innerWidth)).toFixed(3));
    upS(); window.addEventListener('resize', upS); }

  /* ---- Section progress (Josh, 2026-10-17): a segmented bar under the boat bar, one segment per
     home block, filling as you scroll; click to glide there, hover for the block's name ---- */
  { const bar = document.querySelector('[data-ex-now]');
    if (bar && !bar.querySelector('.tts-progress')) {
      const main = document.querySelector('main');
      const secs = [...main.querySelectorAll(':scope > .shopify-section')].slice(1);
      /* Product pages name each segment by what the block IS, not its headline (Josh, 2026-10-19:
         "Sits low. Moves fast" should read OVERVIEW). Keyed by the section's id in the template,
         so every boat and gear page uses the same words; anything unlisted falls back below. */
      const SIMPLE = { main: 'Product', angles: 'Introduction', tour: 'Bow to stern', story: 'Overview', specs: 'Specifications', seat: 'Seat', features: 'In detail', reel: 'Videos', hdpe: 'Materials', walkthrough: 'Walkthrough', warranty: 'Warranty', reviews: 'Reviews', paired: 'Rig it out', water: 'On the water', loop: 'In action', builds: 'Builds', figures: 'By the numbers', fan: 'Features', rod: 'Rod stager', fits: 'Fits' };
      const simple = (sec) => document.body.classList.contains('tt-pt-product') ? SIMPLE[(sec.id.match(/__([a-z0-9_]+)$/) || [])[1]] : null;
      const name0 = (sec) => { const k = sec.querySelector('.tts-kicker, .tt-eyebrow'); const h = sec.querySelector('h2, .tts-h2, .tts-statement__big'); if (sec.querySelector('.tts-par')) return 'On the water'; const t = ((k && k.textContent.trim()) || (h && h.textContent.trim()) || '').split(' · ')[0].replace(/\.$/, ''); return t.replace(/\s+/g, ' ').slice(0, 40); };
      const name = (sec) => simple(sec) || name0(sec);
      const items = secs.map((sec) => ({ sec, label: name(sec) })).filter((x) => x.label);
      /* The boat explorer is the page's introduction (Josh, 2026-10-17): its segment reads
         Introduction and lands on the chosen boat's "Meet the ..." frame, not on Pick your boat. */
      items.forEach((it) => { if (it.sec.querySelector('[data-ex-panel]')) { it.label = 'Introduction'; it.anchor = () => it.sec.querySelector('[data-ex-panel]:not([hidden]) [data-tts-turn]'); } });
      const wrap = document.createElement('nav'); wrap.className = 'tts-progress'; wrap.setAttribute('aria-label', 'Page sections');
      /* Where a block reads complete, as page positions: its stops. One stop for most blocks; a
         block that steps through frames gives one per frame (Josh, 2026-10-24: the arrows "cycle to
         the next image" in Meet the flagship, the seat, and each Bow to stern chapter). The progress
         segments land on a block's first stop; the jump buttons walk every stop in page order. */
      const docY = (el) => window.scrollY + el.getBoundingClientRect().top;
      const vh = () => window.innerHeight;
      /* the boat bar's bottom: everything below the explorer is read under it */
      const barB = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tts-now-b'))
        || ((document.querySelector('.tt-nav')?.getBoundingClientRect().bottom || 0) + 12 + bar.offsetHeight);
      const frameStops = (el) => {
        const span = el.offsetHeight - vh(); if (span <= 0) return [docY(el)];
        if (el.matches('[data-tts-tour]')) { const n = el.querySelectorAll('[data-chapter]').length || 1; return [...Array(n)].map((_, k) => docY(el) + span * (k + 0.5) / n); }
        /* a turn: each frame once its width line has drawn full (--d reaches 1 at 0.6 of its stretch) */
        const n = el.querySelectorAll('[data-frame]').length || 1;
        const run = span - vh() * (+(el.dataset.tail || 0) + +(el.dataset.hold || 0));
        const eff = run > 0 ? run : span;
        return [...Array(n)].map((_, k) => docY(el) + eff * (k + 0.6) / n);
      };
      /* Centre a block's words in the room under the boat bar, measured as if nothing were pinned. */
      const centreOn = (el) => {
        /* as it will read: not pinned, and past its reveal slide (a block not yet revealed still
           sits 36px low, mid-transition, so its offset is read off and taken away) */
        const st = el.style.position; el.style.position = 'static';
        const rs = [...el.children].filter((c) => c.offsetHeight).map((c) => c.getBoundingClientRect());
        el.style.position = st;
        if (!rs.length) return docY(el) - barB();
        let slide = 0; try { slide = new DOMMatrixReadOnly(getComputedStyle(el).transform).m42; } catch (e) { slide = 0; }
        const top = window.scrollY - slide + Math.min(...rs.map((r) => r.top)), h = Math.max(...rs.map((r) => r.bottom)) - Math.min(...rs.map((r) => r.top));
        const b = barB(), room = vh() - b;
        return h >= room ? top - b - 16 : top - b - (room - h) / 2;
      };
      const stopsOf = (it) => {
        const sec = it.sec;
        const ex = sec.querySelector('[data-tts-explorer]');
        if (ex) {
          /* Pick your boat, held at its pinned, centred frame; then the open boat's turn and tour */
          const hold = ex.querySelector('.tts-ex__pickhold'); const panel = ex.querySelector('[data-ex-panel]:not([hidden])');
          return [...(hold ? [docY(hold)] : []), ...(panel ? [...panel.querySelectorAll(':scope > [data-tts-turn], :scope > [data-tts-tour]')].flatMap(frameStops) : [])];
        }
        const turn = sec.querySelector('[data-tts-turn], [data-tts-tour]');
        if (turn) return frameStops(turn);
        const rev = sec.querySelector('.tts-reviews__in'); if (rev) return [centreOn(rev)];
        const par = sec.querySelector('.tts-par');
        /* On the water: each photo with its line up (Josh, 2026-10-25: from "Built for big water."
           the next click goes to the next photo) */
        if (par) { const sc = [...par.querySelectorAll('.tts-par__scene')].filter((f) => f.offsetHeight); return sc.length ? sc.map((f) => docY(f) + vh() * 0.15) : [docY(sec) + vh() * 0.15]; }
        const sticky = sec.querySelector('[data-tts-type], [data-tts-builds], [data-tts-fan], [data-tts-scrub], [data-tts-colours], .tts-statement--hold');
        const span = sticky ? sticky.getBoundingClientRect().height - window.innerHeight : 0;
        if (sticky && span > 0) { const done = sticky.matches('[data-tts-type]') ? 0.9 : sticky.matches('.tts-statement--hold') ? 0.4 : sticky.matches('[data-tts-colours]') ? 0 : 0.02; return [docY(sticky) + span * done]; }
        /* a plain statement (Built for the journey) pauses with its words centred */
        const st = sec.querySelector('.tts-statement__in'); if (st) return [centreOn(st)];
        return [docY(sec) - 120];
      };
      items.forEach((it) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'tts-progress__seg'; b.setAttribute('aria-label', it.label); b.dataset.label = it.label; b.innerHTML = '<span></span>'; b.addEventListener('click', () => {
        const a = it.anchor && it.anchor();
        /* the last segment runs to the foot of the page, so it reads full there */
        const y = a ? docY(a) : it === items[items.length - 1] ? document.documentElement.scrollHeight : stopsOf(it)[0];
        window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
      }); wrap.appendChild(b); it.el = b; });
      bar.querySelector('.tts-ex__now-bar').after(wrap);
      parts.push(() => { items.forEach((it) => { const r = it.sec.getBoundingClientRect(); const vis = !it.sec.querySelector(':scope > [hidden]') && r.height > 2; it.el.hidden = !vis; /* one line on the page decides every segment: segment k fills between its block's top and the
           next block's top, so only one ever fills at once. The line runs from 40% down the screen
           to the screen's bottom as the page ends, so the last segment is full at the footer. */
        const docH = document.documentElement.scrollHeight, maxY = docH - window.innerHeight;
        const line = window.scrollY + window.innerHeight * (0.4 + 0.6 * clamp(window.scrollY / Math.max(1, maxY) * 4 - 3));
        const k = items.indexOf(it);
        const top0 = window.scrollY + r.top;
        const top1 = k < items.length - 1 ? window.scrollY + items[k + 1].sec.getBoundingClientRect().top : docH;
        const f = window.scrollY >= maxY - 2 ? 1 : clamp((line - top0) / Math.max(1, top1 - top0)); it.el.style.setProperty('--f', f.toFixed(3)); it.el.classList.toggle('is-on', f > 0 && f < 1); }); });

      /* ---- Jump buttons (Josh, 2026-10-24): up / down skip to the previous or next block and land
         where it reads complete, the same as tapping its segment. Phones dock them at the end of the
         progress line so nothing floats over content (Bow to stern fills the screen); desktop floats
         them lower right, where the margins are wide. ---- */
      const jump = document.createElement('nav'); jump.className = 'tts-jump'; jump.setAttribute('aria-label', 'Jump between sections');
      jump.innerHTML = '<button type="button" class="tts-jump__btn tts-jump__btn--up" data-dir="-1" aria-label="Previous section"><span></span></button><button type="button" class="tts-jump__btn tts-jump__btn--down" data-dir="1" aria-label="Next section"><span></span></button>';
      /* Every stop on the page, top to bottom. The last one: the foot of the page on desktop; on
         phones the top of the newsletter (Josh, 2026-10-24). */
      const phoneQ = window.matchMedia('(max-width: 749px)');
      const allStops = () => {
        const ys = [0, ...items.filter((it) => !it.el.hidden).flatMap(stopsOf)];
        const maxY = document.documentElement.scrollHeight - vh();
        const news = document.querySelector('.tt-footer__news, .tt-news');
        ys.push(phoneQ.matches && news ? docY(news) - barB() : maxY);
        return ys.map((y) => Math.round(Math.min(maxY, Math.max(0, y)))).sort((a, b) => a - b).filter((y, k, a) => !k || y - a[k - 1] > 24);
      };
      /* A second click while the page is still gliding counts from where the first was going. */
      let aim = null, aimAt = 0, aimFrom = 0;
      const from = () => { const y = window.scrollY;
        const gliding = aim !== null && performance.now() - aimAt < 1400 && y >= Math.min(aimFrom, aim) - 2 && y <= Math.max(aimFrom, aim) + 2;
        return gliding ? aim : y; };
      jump.addEventListener('click', (e) => { const b = e.target.closest('[data-dir]'); if (!b || b.disabled) return;
        const ys = allStops(), y0 = from();
        const y = b.dataset.dir === '1' ? ys.find((v) => v > y0 + 8) : ys.slice().reverse().find((v) => v < y0 - 8);
        if (y === undefined) return;
        aimFrom = window.scrollY; aim = y; aimAt = performance.now();
        window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' }); });
      const phone = window.matchMedia('(max-width: 749px)');
      const place = () => { if (phone.matches) wrap.appendChild(jump); else document.body.appendChild(jump); };
      place(); phone.addEventListener('change', place);
      const [up, down] = jump.querySelectorAll('button');
      parts.push(() => { up.disabled = window.scrollY < 4; down.disabled = window.scrollY >= document.documentElement.scrollHeight - window.innerHeight - 2; });
    }
    /* Pinned blocks on phones start below the boat bar, wherever its height lands (Josh, 2026-10-24:
       the Traverse cards ran into the progress line). The bar is fixed while shown, so its bottom is
       one number for the whole page. */
    if (bar) { let nb = 0; parts.push(() => { if (!bar.classList.contains('is-shown')) return; const b = Math.round(bar.getBoundingClientRect().bottom); if (b !== nb && b > 0) { nb = b; document.documentElement.style.setProperty('--tts-now-b', b + 'px'); } }); }
  }

  /* ---- Ambient depth contours: a fine lake-map pattern seen through drifting fog (Josh, 2026-10-16:
     "it fades in and out and moves in random areas as if looking through fog").
     Contours: marching squares over two octaves of slowly evolving 3D value noise, thin hairlines,
     every fifth an index line. Fog: a third, slower noise field, thresholded so only a few patches
     are open at a time; it masks the lines (destination-in) and wanders on its own clock. ---- */
  document.querySelectorAll('[data-tts-contours]').forEach((cv) => {
    const x = cv.getContext('2d');
    const P = new Uint8Array(512); { const a = [...Array(256).keys()]; let seed = 7; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647; for (let i = 255; i > 0; i -= 1) { const j = (rnd() * (i + 1)) | 0; [a[i], a[j]] = [a[j], a[i]]; } for (let i = 0; i < 512; i += 1) P[i] = a[i & 255]; }
    const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
    const h3 = (i, j, k) => P[(P[(P[i & 255] + j) & 255] + k) & 255] / 255;
    const l = (a, b, t) => a + (b - a) * t;
    const noise = (X, Y, Z) => { const i = Math.floor(X), j = Math.floor(Y), k = Math.floor(Z); const u = fade(X - i), v = fade(Y - j), w = fade(Z - k);
      return l(l(l(h3(i, j, k), h3(i + 1, j, k), u), l(h3(i, j + 1, k), h3(i + 1, j + 1, k), u), v), l(l(h3(i, j, k + 1), h3(i + 1, j, k + 1), u), l(h3(i, j + 1, k + 1), h3(i + 1, j + 1, k + 1), u), v), w); };
    const depth = (X, Y, Z) => noise(X, Y, Z) * 0.6 + noise(X * 2.3 + 17, Y * 2.3 + 5, Z * 1.4) * 0.28 + noise(X * 5.1 + 3, Y * 5.1 + 11, Z * 2) * 0.12;
    const col = getComputedStyle(cv).color;
    const fogC = document.createElement('canvas'); const fx = fogC.getContext('2d');
    let W = 0, H = 0, cell = 8, gx = 0, gy = 0, vals = null, fog = null, fgx = 0, fgy = 0, run = true, last = 0;
    /* A hidden canvas measures 0 x 0, and 48 * H / W is then NaN, which createImageData throws on.
       Reduce Motion hides the page-wide one, and that throw stopped every section after this one
       (Josh's iPhone, 2026-10-17: no hero swipe, the turn stuck, the tour over it). Never size
       below one pixel. */
    const size = () => { const r = cv.getBoundingClientRect(); const d = Math.min(1.5, window.devicePixelRatio || 1); W = cv.width = Math.max(1, Math.round(r.width * d)); H = cv.height = Math.max(1, Math.round(r.height * d));
      cell = Math.max(6, Math.round((r.width < 750 ? 7 : 8) * d)); gx = Math.ceil(W / cell) + 1; gy = Math.ceil(H / cell) + 1; vals = new Float32Array(gx * gy);
      fgx = 48; fgy = Math.max(8, Math.round(48 * H / W)); fogC.width = fgx; fogC.height = fgy; fog = fx.createImageData(fgx, fgy); };
    const LEVELS = 26;
    const SEG = { 1: [[0, 3]], 2: [[0, 1]], 3: [[1, 3]], 4: [[1, 2]], 5: [[0, 1], [2, 3]], 6: [[0, 2]], 7: [[2, 3]], 8: [[2, 3]], 9: [[0, 2]], 10: [[0, 3], [1, 2]], 11: [[1, 2]], 12: [[1, 3]], 13: [[0, 1]], 14: [[0, 3]] };
    const frame = (t) => {
      /* fog first: which areas are open this moment */
      const fz = t / 9000, open = new Uint8Array(fgx * fgy);
      for (let j = 0; j < fgy; j += 1) for (let i = 0; i < fgx; i += 1) {
        const n = noise(i * 0.09 + 40, j * 0.09 + 40, fz) * 0.75 + noise(i * 0.21, j * 0.21, fz * 1.7 + 9) * 0.25;
        let a = (n - 0.535) / 0.16; a = a < 0 ? 0 : a > 1 ? 1 : a * a * (3 - 2 * a);
        fog.data[(j * fgx + i) * 4 + 3] = a * 255; open[j * fgx + i] = a > 0.01;
      }
      fx.putImageData(fog, 0, 0);
      const z = t / 60000, s = 1 / 900;
      for (let j = 0; j < gy; j += 1) for (let i = 0; i < gx; i += 1) vals[j * gx + i] = depth(i * cell * s * 1.6, j * cell * s * 1.6, z);
      x.globalCompositeOperation = 'source-over'; x.clearRect(0, 0, W, H); x.strokeStyle = col; x.lineJoin = 'round';
      const lw = Math.max(0.6, W / 2600);
      for (let L = 1; L <= LEVELS; L += 1) {
        const lv = 0.2 + 0.6 * L / (LEVELS + 1), idx = L % 5 === 0;
        x.globalAlpha = idx ? 0.55 : 0.26; x.lineWidth = idx ? lw * 1.6 : lw;
        x.beginPath();
        for (let j = 0; j < gy - 1; j += 1) { const fj = Math.min(fgy - 1, (j * cell / H * fgy) | 0);
          for (let i = 0; i < gx - 1; i += 1) {
            if (!open[fj * fgx + Math.min(fgx - 1, (i * cell / W * fgx) | 0)]) continue;
            const a = vals[j * gx + i], b = vals[j * gx + i + 1], c = vals[(j + 1) * gx + i + 1], d = vals[(j + 1) * gx + i];
            const m = (a > lv) | ((b > lv) << 1) | ((c > lv) << 2) | ((d > lv) << 3); if (m === 0 || m === 15) continue;
            const X0 = i * cell, Y0 = j * cell;
            const e = [[X0 + cell * (lv - a) / (b - a), Y0], [X0 + cell, Y0 + cell * (lv - b) / (c - b)], [X0 + cell * (lv - d) / (c - d), Y0 + cell], [X0, Y0 + cell * (lv - a) / (d - a)]];
            for (const [p, q] of SEG[m]) { x.moveTo(e[p][0], e[p][1]); x.lineTo(e[q][0], e[q][1]); }
          } }
        x.stroke();
      }
      /* look through the fog: keep the lines only where it is open, soft-edged */
      x.globalAlpha = 1; x.globalCompositeOperation = 'destination-in'; x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
      x.drawImage(fogC, 0, 0, W, H); x.globalCompositeOperation = 'source-over';
    };
    const loop = (t) => { if (!run) return; if (t - last > 80) { last = t; frame(t); } requestAnimationFrame(loop); };
    size(); window.addEventListener('resize', () => { size(); frame(performance.now()); });
    /* Phones draw the lines once and hold them still (2026-10-18 audit: redrawn every 80ms they kept
       a mid-range phone's processor busy for about 1.7s of page load). */
    if (calm || window.matchMedia('(max-width: 749px)').matches) { frame(20000); return; }
    new IntersectionObserver((es) => es.forEach((e) => { const was = run; run = e.isIntersecting; if (run && !was) requestAnimationFrame(loop); })).observe(cv);
    requestAnimationFrame(loop);
  });

  /* ---- Fan: a gallery advanced by the scroll (Josh, 2026-10-17: straight cards that behave like
     physical prints). The scroll picks the focused card; every card then eases to its slot, and
     the stacking order is set by the NEW slots the moment the focus changes, so the card coming
     forward is already on top while it moves: cards slide over each other, never through. ---- */
  /* ---- Story lightbox: one overlay for any story photo set (first used by the card fan) ---- */
  let lb = null;
  const storyLightbox = (items, start) => {
    if (!items.length) return;
    if (!lb) {
      lb = document.createElement('div');
      lb.className = 'tts-lb'; lb.hidden = true;
      lb.setAttribute('role', 'dialog'); lb.setAttribute('aria-modal', 'true'); lb.setAttribute('aria-label', 'Photo');
      lb.innerHTML = '<button type="button" class="tts-lb__close" aria-label="Close">Close <span aria-hidden="true">&times;</span></button>'
        + '<button type="button" class="tts-lb__nav tts-lb__nav--prev" aria-label="Previous photo"><span aria-hidden="true">&#8249;</span></button>'
        + '<figure class="tts-lb__fig"><img class="tts-lb__img" alt=""><figcaption class="tts-lb__cap"></figcaption></figure>'
        + '<button type="button" class="tts-lb__nav tts-lb__nav--next" aria-label="Next photo"><span aria-hidden="true">&#8250;</span></button>'
        + '<span class="tts-num tts-lb__count"></span>';
      document.body.appendChild(lb);
    }
    let i = start; let list = items; let x0 = null;
    const img = lb.querySelector('.tts-lb__img'), cap = lb.querySelector('.tts-lb__cap'), count = lb.querySelector('.tts-lb__count');
    const show = (k) => { i = (k + list.length) % list.length; const c = list[i]; img.src = c.dataset.full; img.alt = c.dataset.cap || ''; cap.textContent = c.dataset.cap || ''; count.textContent = String(i + 1).padStart(2, '0') + ' / ' + String(list.length).padStart(2, '0'); lb.classList.toggle('is-single', list.length < 2); };
    const close = () => { lb.hidden = true; document.documentElement.classList.remove('tts-lb-open'); document.removeEventListener('keydown', key); if (list[i]) list[i].focus({ preventScroll: true }); };
    const key = (e) => { if (e.key === 'Escape') close(); else if (e.key === 'ArrowRight') show(i + 1); else if (e.key === 'ArrowLeft') show(i - 1); };
    lb.onclick = (e) => { if (e.target.closest('.tts-lb__nav--prev')) show(i - 1); else if (e.target.closest('.tts-lb__nav--next')) show(i + 1); else if (!e.target.closest('.tts-lb__img')) close(); };
    lb.ontouchstart = (e) => { x0 = e.touches[0].clientX; };
    lb.ontouchend = (e) => { if (x0 === null) return; const dx = e.changedTouches[0].clientX - x0; x0 = null; if (Math.abs(dx) > 40) show(i + (dx < 0 ? 1 : -1)); };
    show(start);
    lb.hidden = false; document.documentElement.classList.add('tts-lb-open');
    document.addEventListener('keydown', key);
    lb.querySelector('.tts-lb__close').focus({ preventScroll: true });
  };

  document.querySelectorAll('[data-tts-fan]').forEach((root) => {
    const cards = [...root.querySelectorAll('[data-fan-card]')];
    const kpis = [...root.querySelectorAll('[data-fan-kpi]')];
    const n = cards.length; if (!n) return;
    let on = -1;
    const place = (i) => cards.forEach((c, k) => {
      const d = k - i, a = Math.abs(d);
      c.style.setProperty('--dc', Math.max(-2, Math.min(2, d)));
      c.style.setProperty('--a', Math.min(a, 2));
      c.style.zIndex = String(100 - a * 10);
      c.style.opacity = a > 2 ? '0' : '1';
      c.setAttribute('aria-current', String(k === i));
    });
    const go = (k) => { const r = root.getBoundingClientRect(); const span = r.height - window.innerHeight; window.scrollTo({ top: window.scrollY + r.top + span * (n > 1 ? (k + 0.5) / n : 0), behavior: reduce ? 'auto' : 'smooth' }); };
    /* The card in front opens full screen (Josh, 2026-10-19: "Make the built in carousel images
       lightbox clickable"); a card behind still brings itself to the front. */
    const shots = cards.filter((c) => c.dataset.full);
    cards.forEach((c, k) => c.addEventListener('click', () => { if (k === on && c.dataset.full) storyLightbox(shots, shots.indexOf(c)); else go(k); }));
    parts.push(() => {
      const i = Math.min(n - 1, Math.floor(trackProgress(root) * n));
      if (i !== on) { on = i; place(i); kpis.forEach((x, k) => x.classList.toggle('is-on', k === i)); }
    });
    /* Swipe sideways through the cards (Josh, 2026-10-20: "make all pages with the traverse drive
       block sideways scroll through the images"). A horizontal flick on the deck steps to the
       next or previous card through the same scroll the counter and the KPI text already follow;
       a vertical drag still scrolls the page (the deck is touch-action: pan-y). */
    const deck = root.querySelector('[data-fan-deck]');
    if (deck) {
      let sx = 0, sy = 0, live = false;
      deck.addEventListener('touchstart', (e) => { if (e.touches.length !== 1) { live = false; return; } sx = e.touches[0].clientX; sy = e.touches[0].clientY; live = true; }, { passive: true });
      deck.addEventListener('touchend', (e) => {
        if (!live) return; live = false;
        const t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
        if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        go(Math.max(0, Math.min(n - 1, on + (dx < 0 ? 1 : -1))));
        deck.dataset.swiped = '1';
      }, { passive: true });
      /* the tap that ends a swipe must not also open the card full screen */
      deck.addEventListener('click', (e) => { if (deck.dataset.swiped) { delete deck.dataset.swiped; e.stopPropagation(); e.preventDefault(); } }, true);
    }
    place(0);
  });

  /* ---- Turn: frames cross-fade as the visitor scrolls ---- */
  document.querySelectorAll('[data-tts-turn]').forEach((root) => {
    const frames = [...root.querySelectorAll('[data-frame]')];
    const copies = [...root.querySelectorAll('[data-copy]')];
    const dots = [...root.querySelectorAll('[data-dot]')];
    const n = frames.length;
    let last = -1;
    /* A hand-off tail (data-tail, in screens) runs after the last frame: the frames finish early
       and the last line keeps growing through it while the next section slides up over it. */
    const tail = +(root.dataset.tail || 0);
    /* A hold (data-hold, in screens) between the last frame and the tail: the frames finish that
       much sooner and the last one stays put before the zoom (Josh, 2026-10-17). */
    const hold = +(root.dataset.hold || 0);
    /* Zoom through the O (Josh, 2026-10-16): the first O of the hand-off line is wrapped, and as the
       line grows the script slides it until that O sits dead centre, so the visitor passes through it. */
    const handH2 = root.querySelector('.tts-turn__line--handoff .tts-h2');
    let zoomO = null;
    if (tail && handH2) {
      const tn = [...handH2.childNodes].find((nd) => nd.nodeType === 3 && /o/i.test(nd.data));
      if (tn) {
        const i = tn.data.search(/o/i);
        const after = tn.splitText(i);
        after.splitText(1);
        zoomO = document.createElement('span');
        zoomO.className = 'tts-zoom-o';
        after.replaceWith(zoomO);
        zoomO.appendChild(after);
      }
    }
    const centreO = (out) => {
      if (!zoomO) return;
      if (out <= 0) { handH2.style.transform = ''; return; }
      const cur = handH2.style.transform.match(/-?[\d.]+/g)?.map(Number) || [0, 0];
      const r = zoomO.getBoundingClientRect();
      const nx = r.left + r.width / 2 - (cur[0] || 0);
      const ny = r.top + r.height * 0.52 - (cur[1] || 0);
      const e = Math.min(1, out / 0.18);
      const k = e * e * (3 - 2 * e);
      handH2.style.transform = `translate(${((window.innerWidth / 2 - nx) * k).toFixed(1)}px, ${((window.innerHeight / 2 - ny) * k).toFixed(1)}px)`;
    };
    parts.push(() => {
      const r = root.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      const tailPx = tail * window.innerHeight;
      const y = -r.top;
      const holdPx = hold * window.innerHeight;
      const p = span > tailPx + holdPx ? clamp(y / (span - tailPx - holdPx)) : trackProgress(root);
      root.style.setProperty('--p', p.toFixed(4));
      if (tail) {
        /* the zoom waits for the last frame to finish (its width line done and held), then runs
           through the tail (Josh, 2026-10-17) */
        const start = span - tailPx;
        const out = clamp((y - start) / (span - start));
        /* --out runs with Reduce Motion too: the scroll drives it, and without it the last frame
           and its words never leave and the tour lands on top of them. Only the zoom is skipped. */
        root.style.setProperty('--out', out.toFixed(4));
        if (!reduce) centreO(out);
      }
      const i = Math.min(n - 1, Math.floor(p * n));
      /* Scrub, not a slideshow: each frame drifts with the scroll while it is on (--l runs
         -0.5..0.5 across its screen) and its dimension line draws in (--d 0..1). */
      frames.forEach((f, k) => {
        const l = clamp(p * n - k - 0.5, -0.5, 0.5);
        f.style.setProperty('--l', reduce ? '0' : l.toFixed(4));
        f.style.setProperty('--d', clamp((l + 0.5) * 2.2).toFixed(4));
      });

      if (i === last) return;
      last = i;
      frames.forEach((f, k) => f.classList.toggle('is-on', k === i));
      copies.forEach((c, k) => c.classList.toggle('is-on', k === i));
      dots.forEach((d, k) => d.classList.toggle('is-on', k <= i));
    });
  });

  /* ---- Tour: chapters light their stretch of the boat and their callouts ---- */
  document.querySelectorAll('[data-tts-tour]').forEach((root) => {
    const data = JSON.parse(root.querySelector('[data-tour-json]')?.textContent || 'null');
    const stage = root.querySelector('[data-tour-pins]');
    const chapters = [...root.querySelectorAll('[data-chapter]')];
    const zone = root.querySelector('[data-tour-zone]');
    const list = root.querySelector('[data-tour-list]');
    if (!data || !stage || !chapters.length) return;
    /* The data is measured on the boat standing bow-up; the stage lays it on its side, bow left. */
    /* A picture longer than the hull it was measured on (a rudder off the stern) shrinks the
       callouts toward the bow: data-tour-scale is the hull's share of the picture's length. */
    const k = (parseFloat(root.dataset.tourScale) || 100) / 100;
    /* A fourth item ties a callout to the package toggle: 'drive' or 'hull' (Josh, 2026-10-19). */
    const pins = data.pins.map(([l, t, label, pkg]) => ({ x: t * k, y: 100 - l, label, pkg }));
    let pkg = root.dataset.pkg || 'drive';
    let solo = false;
    const fits = (pin) => !pin.pkg || pin.pkg === pkg;
    pins.forEach((pin) => {
      const d = document.createElement('span');
      d.className = 'tts-pin';
      d.style.left = pin.x + '%';
      d.style.top = pin.y + '%';
      pin.el = d;
      stage.appendChild(d);
    });
    const ranges = chapters.map((c) => [+c.dataset.from * k, +c.dataset.to * k]);
    let last = -1;
    let hot = 0;
    let jumpTo = null, jumpUntil = 0;
    const show = (i, again) => {
      if (i === last && !again) return;
      last = i;
      const [a, b] = ranges[i];
      chapters.forEach((c, k) => c.classList.toggle('is-on', k === i));
      zone.style.left = a + '%';
      zone.style.width = (b - a) + '%';
      let num = 0;
      const items = [];
      pins.forEach((pin) => {
        const on = fits(pin) && pin.x >= a && pin.x < b;
        pin.el.style.display = fits(pin) ? '' : 'none';
        pin.el.classList.toggle('is-on', on);
        pin.el.classList.remove('is-hot');
        pin.el.textContent = on ? String(++num) : '';
        if (on) items.push(`<li><button type="button" class="tts-tour__row" data-n="${num}"><span class="tts-num">${String(num).padStart(2, '0')}</span>${pin.label}</button></li>`);
      });
      list.innerHTML = items.join('');
      /* The list keeps the height of the longer package's list for this stretch, so switching
         With drive / Hull only never moves the boat (Josh, 2026-10-19: the 140T jumped). */
      list.style.minHeight = '';
      if (root.querySelector('[data-pkg]')) {
        const other = pkg === 'hull' ? 'drive' : 'hull';
        const rows = pins.filter((pin) => (!pin.pkg || pin.pkg === other) && pin.x >= a && pin.x < b)
          .map((pin, j) => `<li><button type="button" class="tts-tour__row"><span class="tts-num">${String(j + 1).padStart(2, '0')}</span>${pin.label}</button></li>`);
        const ghost = list.cloneNode(false);
        ghost.removeAttribute('data-tour-list'); ghost.removeAttribute('aria-live'); ghost.setAttribute('aria-hidden', 'true');
        ghost.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:${list.getBoundingClientRect().width}px;min-height:0`;
        ghost.innerHTML = rows.join('');
        list.parentNode.appendChild(ghost);
        const h = Math.max(ghost.getBoundingClientRect().height, list.getBoundingClientRect().height);
        ghost.remove();
        list.style.minHeight = h + 'px';
      }
      hot = 0;
    };
    window.addEventListener('resize', () => { if (last >= 0) show(last, true); });
    /* A row picked in the list lights its number on the boat, and a number picked on the boat
       lights its row (Josh, 2026-10-16). Picking it again lets go. */
    const lightUp = (n) => {
      hot = hot === n ? 0 : n;
      const lit = pins.filter((pin) => pin.el.classList.contains('is-on'));
      lit.forEach((pin, j) => pin.el.classList.toggle('is-hot', j + 1 === hot));
      list.querySelectorAll('[data-n]').forEach((r) => { const on = +r.dataset.n === hot; r.classList.toggle('is-hot', on); r.setAttribute('aria-pressed', String(on)); });
    };
    list.addEventListener('click', (e) => { const r = e.target.closest('[data-n]'); if (r) lightUp(+r.dataset.n); });
    /* Tapping the boat outside the lit stretch moves the tour to the stretch under the finger. */
    /* Hovering another stretch of the boat shows it faintly, to say it can be clicked */
    /* Phones stand the boat bow-up (rotated a quarter turn), so the hull runs down the screen. */
    const tourBoat = root.querySelector('.tts-tour__boat');
    const upright = () => window.matchMedia('(max-width: 749px)').matches && root.classList.contains('tts-ex__tour');
    const along = (e) => { const r = stage.getBoundingClientRect(); return upright() ? (e.clientY - r.top) / r.height * 100 : (e.clientX - r.left) / r.width * 100; };
    const fitUpright = () => { if (!tourBoat) return; if (upright()) tourBoat.style.setProperty('--tb-w', stage.offsetHeight + 'px'); else tourBoat.style.removeProperty('--tb-w'); };
    stage.querySelectorAll('img').forEach((im) => im.complete ? fitUpright() : im.addEventListener('load', fitUpright));
    window.addEventListener('resize', fitUpright); fitUpright();
    const hz = document.createElement('span'); hz.className = 'tts-tour__zone tts-tour__zone--hover'; hz.setAttribute('aria-hidden', 'true'); stage.appendChild(hz);
    stage.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch') return;
      const x = along(e);
      const k = ranges.findIndex(([a, b]) => x >= a && x < b);
      if (k < 0 || k === last || e.target.closest('.tts-pin.is-on')) { hz.classList.remove('is-on'); stage.style.cursor = ''; return; }
      const [a, b] = ranges[k]; hz.style.left = a + '%'; hz.style.width = (b - a) + '%'; hz.classList.add('is-on'); stage.style.cursor = 'pointer';
    });
    stage.addEventListener('pointerleave', () => hz.classList.remove('is-on'));
    stage.addEventListener('dragstart', (e) => e.preventDefault());
    stage.addEventListener('contextmenu', (e) => e.preventDefault());
    stage.addEventListener('click', (e) => {
      if (e.target.closest('.tts-pin.is-on')) return;
      const x = along(e);
      const k = ranges.findIndex(([a, b]) => x >= a && x < b);
      if (k < 0 || k === last) return;
      const rr = root.getBoundingClientRect();
      const span = rr.height - window.innerHeight;
      /* light the target now and hold it while the page glides there, so the stretches in between
         don't flash past (Josh, 2026-10-17) */
      jumpTo = k; jumpUntil = performance.now() + 1600; show(k);
      window.scrollTo({ top: window.scrollY + rr.top + span * ((k + 0.5) / chapters.length), behavior: reduce ? 'auto' : 'smooth' });
    });
    /* Seating toggle (tandems): one hull, rigged for two or for one. Package toggle: the same hull
       with the drive in or out. Both pick from the same pictures, so they work together. */
    const views = [...stage.querySelectorAll('[data-view]')];
    const pickView = () => {
      const want = solo && pkg === 'hull' ? ['solo-hull', 'hull', 'solo'] : solo ? ['solo'] : pkg === 'hull' ? ['hull'] : [];
      const pick = want.map((w) => views.find((v) => v.dataset.view === w)).find(Boolean);
      views.forEach((v) => v.classList.toggle('is-shown', v === pick));
      root.classList.toggle('has-alt', !!pick);
    };
    const pressOnly = (btns, btn) => btns.forEach((o) => { const on = o === btn; o.classList.toggle('is-on', on); o.setAttribute('aria-pressed', String(on)); });
    const seatBtns = [...root.querySelectorAll('[data-seats]')];
    seatBtns.forEach((btn) => btn.addEventListener('click', () => {
      solo = btn.dataset.seats === 'one';
      root.classList.toggle('is-solo', solo);
      pressOnly(seatBtns, btn);
      pickView();
    }));
    const pkgBtns = [...root.querySelectorAll('[data-pkg]')];
    pkgBtns.forEach((btn) => btn.addEventListener('click', () => {
      if (btn.dataset.pkg === pkg) return;
      pkg = btn.dataset.pkg;
      root.dataset.pkg = pkg;
      root.classList.toggle('is-hull', pkg === 'hull');
      pressOnly(pkgBtns, btn);
      pickView();
      if (last >= 0) show(last, true);
    }));
    stage.addEventListener('click', (e) => { const d = e.target.closest('.tts-pin.is-on'); if (d && d.textContent) lightUp(+d.textContent); });
    show(0);
    parts.push(() => {
      const p = trackProgress(root);
      root.style.setProperty('--p', p.toFixed(4));
      const ci = Math.min(chapters.length - 1, Math.floor(p * chapters.length));
      if (jumpTo !== null) { if (ci === jumpTo || performance.now() > jumpUntil) jumpTo = null; else return; }
      show(ci);
    });
  });

  /* ---- Lineup: one boat per stretch of scroll; a tapped name scrolls to its stretch ---- */
  document.querySelectorAll('[data-tts-lineup]').forEach((root) => {
    const boats = [...root.querySelectorAll('[data-boat]')];
    const names = [...root.querySelectorAll('[data-pick]')];
    const n = boats.length;
    let last = -1;
    const show = (i) => {
      if (i === last) return;
      const dir = i > last ? 1 : -1;
      last = i;
      boats.forEach((b, k) => { b.classList.toggle('is-on', k === i); b.dataset.dir = k < i ? '-1' : '1'; });
      names.forEach((nm, k) => nm.classList.toggle('is-on', k === i));
      root.style.setProperty('--dir', dir);
    };
    names.forEach((nm) => nm.addEventListener('click', () => {
      const k = +nm.dataset.pick;
      const r = root.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      window.scrollTo({ top: window.scrollY + r.top + span * ((k + 0.5) / n), behavior: reduce ? 'auto' : 'smooth' });
    }));
    show(0);
    parts.push(() => {
      const p = trackProgress(root);
      /* each view's own stretch, -0.5..0.5: the numeral rides against the photo (parallax) */
      views.forEach((v, k) => v.style.setProperty('--lp', reduce ? '0' : clamp(p * n - k - 0.5, -0.5, 0.5).toFixed(4)));
      show(Math.min(n - 1, Math.floor(p * n)));
    });
  });

  /* ---- Explorer: pick a model, its turn and tour take the stage ---- */
  document.querySelectorAll('[data-tts-explorer]').forEach((root) => {
    const panels = [...root.querySelectorAll('[data-ex-panel]')];
    const picks = [...root.querySelectorAll('[data-ex-pick]')];
    /* Where the visitor is inside the open boat's walkthrough: which part, and how far through. */
    const placeIn = (panel) => {
      const kids = [...panel.children];
      const k = kids.findIndex((c) => { const r = c.getBoundingClientRect(); return r.top <= 1 && r.bottom > 1; });
      if (k < 0) return null;
      const r = kids[k].getBoundingClientRect();
      const span = r.height - window.innerHeight;
      return { k, f: span > 0 ? clamp(-r.top / span) : 0, px: -r.top };
    };
    const choose = (key, jump) => {
      const panel = panels.find((pn) => pn.dataset.exPanel === key);
      if (!panel) return;
      /* Changing boat mid-walkthrough keeps your place (Josh, 2026-10-16): same part, same share
         of the way through, on the new boat. From anywhere else it starts that boat from the top. */
      const was = panels.find((pn) => !pn.hidden);
      const at = jump && was && was !== panel ? placeIn(was) : null;
      panels.forEach((pn) => { pn.hidden = pn !== panel; });
      picks.forEach((b) => { const on = b.dataset.exPick === key; b.classList.toggle('is-on', on); if (b.hasAttribute('aria-selected')) b.setAttribute('aria-selected', String(on)); });
      root.querySelectorAll('[data-now]').forEach((n) => { n.hidden = n.dataset.now !== key; });
      /* Hardshell or inflatable: statements further down the page answer to it (show_for). */
      document.documentElement.dataset.boatKind = picks.find((b) => b.dataset.exPick === key)?.dataset.kind || 'rigid';
      document.documentElement.dataset.boat = key;
      document.dispatchEvent(new CustomEvent('tts:boat', { detail: { key, name: picks.find((b) => b.dataset.exPick === key)?.dataset.name || '' } }));
      setDrop(false);
      try { history.replaceState(null, '', '#boat-' + key); } catch (e) {}
      if (at) {
        const kid = panel.children[at.k];
        const r = kid.getBoundingClientRect();
        const span = r.height - window.innerHeight;
        window.scrollTo({ top: window.scrollY + r.top + (span > 0 ? at.f * span : Math.min(at.px, r.height - 1)), behavior: 'auto' });
      } else if (jump) window.scrollTo({ top: window.scrollY + panel.getBoundingClientRect().top - 40, behavior: reduce ? 'auto' : 'smooth' });
      ask();
    };
    let refit = null;
    /* The now bar: shown once the lineup has scrolled away, folds the lineup down on a click. */
    const now = root.querySelector('[data-ex-now]');
    const toggle = root.querySelector('[data-ex-toggle]');
    const drop = root.querySelector('[data-ex-drop]');
    function setDrop(open) {
      if (!drop || !toggle) return;
      drop.hidden = !open;
      /* Re-measure on every open: the display face may have arrived since the page laid out. */
      if (open && typeof refit === 'function') refit();
      toggle.setAttribute('aria-expanded', String(open));
      now.classList.toggle('is-open', open);
    }
    /* Change boat glides back up to Pick your boat, held at its pinned, centred frame (Josh, 2026-10-17). */
    toggle?.addEventListener('click', () => {
      if (drop) return setDrop(drop.hidden);
      const hold = root.querySelector('.tts-ex__pickhold') || root;
      window.scrollTo({ top: window.scrollY + hold.getBoundingClientRect().top, behavior: reduce ? 'auto' : 'smooth' });
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setDrop(false); });
    document.addEventListener('click', (e) => { if (now && !now.contains(e.target)) setDrop(false); });
    const mainPicker = root.querySelector('.tts-ex__picker');
    /* Phones: the lineup runs off the right edge, so a soft fade and a small arrow say there is
       more; they leave once the row has been scrolled to its end (Josh, 2026-10-18). */
    const pickStage = root.querySelector('.tts-ex__pickstage');
    if (mainPicker && pickStage) {
      const more = document.createElement('span'); more.className = 'tts-ex__more'; more.setAttribute('aria-hidden', 'true'); more.innerHTML = '<span></span>';
      pickStage.appendChild(more);
      const placeMore = () => {
        more.style.top = mainPicker.offsetTop + 'px'; more.style.height = mainPicker.offsetHeight + 'px';
        more.classList.toggle('is-end', mainPicker.scrollLeft + mainPicker.clientWidth >= mainPicker.scrollWidth - 12);
      };
      mainPicker.addEventListener('scroll', placeMore, { passive: true });
      window.addEventListener('resize', placeMore); window.addEventListener('load', placeMore); placeMore();
    }
    parts.push(() => {
      if (!now || !mainPicker) return;
      const pr = mainPicker.getBoundingClientRect();
      /* Once the lineup has gone by, the bar stays for the rest of the page (Josh, 2026-10-16). */
      const show = pr.bottom < 90;
      now.classList.toggle('is-shown', show);
      if (!show) setDrop(false);
    });
    picks.forEach((b) => b.addEventListener('click', () => choose(b.dataset.exPick, true)));
    document.documentElement.dataset.boatKind = picks.find((b) => b.classList.contains('is-on'))?.dataset.kind || 'rigid';
    { const on = picks.find((b) => b.classList.contains('is-on')); if (on) { document.documentElement.dataset.boat = on.dataset.exPick; document.dispatchEvent(new CustomEvent('tts:boat', { detail: { key: on.dataset.exPick, name: on.dataset.name || '' } })); } }
    /* The boat row spans exactly the model bar's width (Josh, 2026-10-16): the boats grow until the
       row fills it, and the groups spread across what is left. Wide screens only; phones scroll. */
    const picker = root.querySelector('.tts-ex__picker');
    const head = root.querySelector('.tts-ex__head');
    const fit = () => {
      if (!picker || !head) return;
      picker.style.removeProperty('--row-h'); picker.style.removeProperty('width'); picker.classList.remove('is-fit');
      root.querySelector('[data-ex-now]')?.style.removeProperty('width');
      root.querySelector('.tts-ex__picker--drop')?.style.removeProperty('--row-h');
      if (window.innerWidth < 1200) return;
      /* The span the model bar had: the page's content width, capped at 1184px. */
      const hs = getComputedStyle(head);
      const target = Math.min(1184, head.getBoundingClientRect().width - parseFloat(hs.paddingLeft) - parseFloat(hs.paddingRight));
      const groups = [...picker.querySelectorAll('.tts-ex__group')];
      const natural = groups.reduce((w, g) => w + g.getBoundingClientRect().width, 0);
      const figs = [...picker.querySelectorAll('.tts-ex__fig')];
      const figW = figs.reduce((w, f) => w + f.getBoundingClientRect().width, 0);
      const gaps = target - natural;
      const h0 = parseFloat(getComputedStyle(picker).getPropertyValue('--row-h')) || figs[0]?.getBoundingClientRect().height || 300;
      if (figW > 0 && gaps > 0) {
        const grow = Math.min(1.35, 1 + (gaps * 0.7) / figW);
        picker.style.setProperty('--row-h', Math.round(h0 * grow) + 'px');
      }
      picker.style.width = Math.round(target) + 'px';
      picker.classList.add('is-fit');
      /* Measure, don't guess: shrink the boats until the row really fits its span. */
      /* Fits = every series ends inside the container's padding edge (scrollWidth ignores padding). */
      const fitsIn = (el) => {
        const r = el.getBoundingClientRect(); const pr = parseFloat(getComputedStyle(el).paddingRight) || 0;
        const right = Math.max(...[...el.children].map((k) => k.getBoundingClientRect().right));
        return right <= r.right - pr + 0.5;
      };
      const shrinkToFit = (el) => {
        for (let n = 0; n < 30 && !fitsIn(el); n++) {
          const cur = parseFloat(getComputedStyle(el).getPropertyValue('--row-h')) || 300;
          el.style.setProperty('--row-h', Math.floor(cur * 0.97) + 'px');
        }
      };
      shrinkToFit(picker);
      /* The now bar and its drop-down lineup take exactly the same span and size as Pick your boat. */
      const nowEl = root.querySelector('[data-ex-now]');
      const dropPicker = root.querySelector('.tts-ex__picker--drop');
      if (nowEl) nowEl.style.width = Math.round(target) + 'px';
      if (dropPicker) {
        /* a hair smaller than the original, so the drop-down's own border never forces a scroll */
        const rh = parseFloat(getComputedStyle(picker).getPropertyValue('--row-h')) || 300;
        dropPicker.style.setProperty('--row-h', Math.floor(rh * 0.97) + 'px');
        dropPicker.classList.add('is-fit');
        const dropEl = root.querySelector('[data-ex-drop]');
        const wasHidden = dropEl.hidden; dropEl.hidden = false;
        shrinkToFit(dropPicker);
        dropEl.hidden = wasHidden;
      }
    };
    refit = fit;
    if (document.readyState === 'complete') fit(); else window.addEventListener('load', fit);
    /* The lineup is sized by its words as well as its boats, so size it again once the fonts land. */
    document.fonts?.ready.then(() => requestAnimationFrame(fit));
    document.fonts?.addEventListener?.('loadingdone', () => requestAnimationFrame(fit));
    window.addEventListener('resize', () => requestAnimationFrame(fit));
    root.querySelectorAll('[data-ex-top]').forEach((b) => b.addEventListener('click', () => {
      window.scrollTo({ top: window.scrollY + root.getBoundingClientRect().top - 40, behavior: reduce ? 'auto' : 'smooth' });
    }));
    /* #tour-<boat> (Josh, 2026-10-19: the hero's "Tour the <boat>" scrolls down to the tour): picks
       that boat, then lands on the start of its feature tour rather than on "Meet the ...". */
    const toTour = (key) => {
      choose(key, false);
      const panel = panels.find((pn) => pn.dataset.exPanel === key);
      const tour = panel && panel.querySelector('[data-tts-tour]');
      if (!tour) return;
      requestAnimationFrame(() => window.scrollTo({ top: window.scrollY + tour.getBoundingClientRect().top, behavior: reduce ? 'auto' : 'smooth' }));
    };
    const fromHash = (jump) => {
      const t = location.hash.match(/^#tour-([\w-]+)/);
      if (t) return toTour(t[1]);
      const m = location.hash.match(/^#boat-([\w-]+)/); if (m) choose(m[1], jump);
    };
    fromHash(false);
    window.addEventListener('hashchange', () => fromHash(true));
  });

  /* ---- Builds: the section pins and the scroll steps through the builds; a pick scrolls to its stretch ---- */
  document.querySelectorAll('[data-tts-builds]').forEach((root) => {
    const tabs = [...root.querySelectorAll('[data-build]')];
    const views = [...root.querySelectorAll('[data-build-view]')];
    const n = tabs.length;
    let i = -1;
    const show = (k) => {
      if (k === i) return;
      i = k;
      tabs.forEach((t, j) => { t.classList.toggle('is-on', j === k); t.setAttribute('aria-selected', String(j === k)); });
      views.forEach((v, j) => v.classList.toggle('is-on', j === k));
    };
    show(0);
    tabs.forEach((t, k) => t.addEventListener('click', () => {
      const r = root.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      if (span <= 0) { show(k); return; }
      window.scrollTo({ top: window.scrollY + r.top + span * ((k + 0.5) / n), behavior: reduce ? 'auto' : 'smooth' });
    }));
    parts.push(() => show(Math.min(n - 1, Math.floor(trackProgress(root) * n))));
  });

  /* ---- Hero slideshow: advance on a timer the progress bar shows; pause while hovered, focused or off screen ---- */
  document.querySelectorAll('[data-tts-hero-show]').forEach((root) => {
    const slides = [...root.querySelectorAll('[data-hero-slide]')];
    const tabs = [...root.querySelectorAll('[data-hero-go]')];
    if (slides.length < 2) return;
    const ms = (parseFloat(root.dataset.interval) || 7) * 1000;
    root.style.setProperty('--hero-int', ms + 'ms');
    let i = 0; let timer = null; let held = false; let seen = true;
    const go = (k) => {
      const prev = i;
      i = (k + slides.length) % slides.length;
      /* Plain cross-fade between slides: no exit animation to snap back when it ends. */
      /* Restart the slide's own timeline (enter, turn, hold) every time it comes on. */
      slides.forEach((s, j) => { if (j !== i) s.classList.remove('is-on'); });
      const now = slides[i];
      fetchRest();
      now.classList.remove('is-on'); void now.offsetWidth; now.classList.add('is-on');
      tabs.forEach((t, j) => { t.classList.toggle('is-on', j === i); t.classList.toggle('was', j < i); t.setAttribute('aria-selected', String(j === i)); });
      arm();
    };
    let ready = document.readyState === 'complete';
    const arm = () => { clearTimeout(timer); if (ready && !calm && !held && seen) timer = setTimeout(() => go(i + 1), ms); };
    const hold = (on) => { held = on; root.classList.toggle('is-paused', on); if (on) clearTimeout(timer); else arm(); };
    tabs.forEach((t) => t.addEventListener('click', () => go(+t.dataset.heroGo)));
    /* No hover pause: a resting cursor sits on a full-screen hero, and the show would never move (Josh, 2026-10-16). */
    /* Keyboard focus pauses (someone is reading with the keyboard); a mouse click on a tab does not. */
    root.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) hold(true); });
    root.addEventListener('focusout', () => hold(false));
    root.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') go(i + 1); if (e.key === 'ArrowLeft') go(i - 1); });
    /* Swipe left or right to change slides; the slides cross-fade, they do not slide (Josh,
       2026-10-18: "fading content, not sliding content"). A mostly vertical drag is a scroll. */
    let sx = null, sy = 0;
    root.addEventListener('touchstart', (e) => { const t = e.touches[0]; sx = t.clientX; sy = t.clientY; }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (sx === null) return; const t = e.changedTouches[0]; const dx = t.clientX - sx, dy = t.clientY - sy; sx = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) go(i + (dx < 0 ? 1 : -1));
    }, { passive: true });
    /* The first slide is already on from the server, and its entrance already played on first
       paint, so boot does not replay it (that hid and re-showed the picture). The show only starts
       its clock once the page has finished loading (below): a slide that changes while a phone is still
       loading becomes the page's new "largest paint", and the speed score counted the third slide
       as the moment the page was ready (2026-10-20). The first tab's bar restarts with the clock so
       the two stay in step. */
    /* Later slides cannot show before the clock starts, so their pictures wait for the load
       event (tt-story-hero-slide renders them as data-srcset) and the first slide's picture
       downloads alone. They still land well before the first change. */
    const fetchRest = () => root.querySelectorAll('[data-srcset], [data-src]').forEach((el) => {
      if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
      if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
    });
    const start = () => {
      ready = true;
      fetchRest();
      const t = tabs[0];
      if (t && i === 0) { t.classList.remove('is-on'); void t.offsetWidth; t.classList.add('is-on'); }
      arm();
    };
    new IntersectionObserver((es) => es.forEach((e) => { seen = e.isIntersecting; root.classList.toggle('is-paused', !seen || held); if (seen) arm(); else clearTimeout(timer); }), { threshold: 0.3 }).observe(root);
    /* "Loaded" means quiet, not the load event: tag and pixel scripts keep fetching for seconds
       after it, and a slide that changes in that tail still counted. Wait until nothing has
       finished downloading for 1.5s (never more than 15s past load), then start the clock. */
    const quiet = () => {
      fetchRest();
      if (!('PerformanceObserver' in window)) { start(); return; }
      let last = performance.now(); const t0 = last;
      const po = new PerformanceObserver(() => { last = performance.now(); });
      try { po.observe({ type: 'resource' }); } catch (e) { start(); return; }
      const tick = () => { const n = performance.now(); if (n - last >= 1500 || n - t0 >= 15000) { po.disconnect(); start(); } else setTimeout(tick, 250); };
      setTimeout(tick, 250);
    };
    if (ready) start(); else window.addEventListener('load', quiet, { once: true });
  });

  /* ---- Fade: hero copy eases out as it is scrolled away ---- */
  const fades = [...document.querySelectorAll('[data-tts-fade]')];
  if (!reduce) parts.push(() => fades.forEach((el) => {
    const r = el.getBoundingClientRect();
    const o = clamp(1 - Math.max(0, -r.top) / (window.innerHeight * 0.45));
    el.style.setProperty('--fade', o.toFixed(3));
  }));

  /* ---- Parallax: anything with data-tts-par moves at its own rate against the scroll ---- */
  const movers = [...document.querySelectorAll('[data-tts-par]')];
  const glides = [...document.querySelectorAll('[data-tts-glide]')];
  if (!reduce) {
    parts.push(() => {
      movers.forEach((el) => {
        const host = el.closest('[data-tts-host]') || el.parentElement;
        const p = passProgress(host) - 0.5;
        const axis = el.dataset.ttsAxis || 'y';
        const amt = parseFloat(el.dataset.ttsPar) * 100 * p;
        el.style.transform = axis === 'x' ? `translate3d(${amt}vw,0,0)` : `translate3d(0,${amt}vh,0)`;
      });
      glides.forEach((el) => {
        const host = el.closest('[data-tts-host]') || el;
        const p = clamp(passProgress(host) * 2.2);
        el.style.setProperty('--in', p.toFixed(4));
      });
    });
  } else {
    glides.forEach((el) => el.style.setProperty('--in', '1'));
  }

  /* ---- Reveal: blocks fade up once as they enter ---- */
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
  }), { threshold: 0.2 });
  document.querySelectorAll('[data-tts-reveal]').forEach((el) => (reduce ? el.classList.add('is-in') : io.observe(el)));

  let ticking = false;
  const tick = () => { ticking = false; parts.forEach((f) => f()); };
  ask = () => { if (!ticking) { ticking = true; requestAnimationFrame(tick); } };
  window.addEventListener('scroll', ask, { passive: true });
  window.addEventListener('resize', ask);
  tick();
})();
